import { client } from '@/client';
import {
  AccountAdminTask,
  CreateAccountAdminTaskMutation,
  DeleteAccountAdminTaskMutation,
  ListAdminTasksRequest,
  ListAdminTasksResponse,
  UpdateAccountAdminTaskMutation,
  UpdateAccountAdminTaskRequest,
} from '@/client/admin-tasks';
import { MessageResponseModel } from '@/client/models';
import { GenericForbiddenApiError } from '@/hooks/query/types';
import {
  useMutation,
  useQuery,
  UseQueryOptions,
  UseQueryResult,
} from 'react-query';

const queryKey = 'accountAdminTasks';
const singleQueryKey = 'accountAdminTask';

export const useAccountAdminTasks = (
  accountId: string,
  params: UseQueryOptions & ListAdminTasksRequest = {},
): UseQueryResult<ListAdminTasksResponse> & {
  accountAdminTasks: ListAdminTasksResponse | undefined;
} => {
  const {
    retry = false,
    retryDelay = 500,
    staleTime = 0,
    cacheTime = 0,
    ...restOptions
  } = params;

  const { data, ...rest } = useQuery(
    [
      queryKey,
      accountId,
      JSON.stringify(params.filters),
      JSON.stringify(params.sort),
    ],
    () => client.accountAdminTasks.getAccountAdminTasks(params, accountId),
    {
      retry,
      retryDelay,
      staleTime,
      cacheTime,
      enabled: !!accountId,
      ...(restOptions as any),
    },
  );

  return {
    accountAdminTasks: data,
    ...(rest as UseQueryResult<ListAdminTasksResponse>),
  };
};

export const useAccountAdminTask = (
  params: UseQueryOptions & {
    taskId: string;
    accountId: string;
  },
): UseQueryResult<AccountAdminTask> & {
  accountAdminTask: AccountAdminTask | undefined;
} => {
  const {
    taskId,
    accountId,
    retry = false,
    retryDelay = 500,
    staleTime = 0,
    cacheTime = 0,
    ...restOptions
  } = params;

  const { data, ...rest } = useQuery(
    [singleQueryKey, taskId, accountId],
    () => client.accountAdminTasks.getAccountAdminTask(taskId, accountId),
    {
      retry,
      retryDelay,
      staleTime,
      cacheTime,
      enabled: !!taskId && !!accountId,
      ...(restOptions as any),
    },
  );

  return {
    accountAdminTask: data,
    ...(rest as UseQueryResult<AccountAdminTask>),
  };
};

export const useCreateAccountAdminTask = (): {
  create: (
    payload: AccountAdminTask & { accountId: string },
  ) => Promise<AccountAdminTask>;
} & CreateAccountAdminTaskMutation => {
  const { mutateAsync, ...rest } = useMutation<
    AccountAdminTask,
    GenericForbiddenApiError,
    AccountAdminTask & { accountId: string }
  >(
    'createAccountAdminTask',
    (payload: AccountAdminTask & { accountId: string }) => {
      const { accountId, ...request } = payload;
      return client.accountAdminTasks.createAccountAdminTask(
        request,
        accountId,
      );
    },
  );

  return { create: mutateAsync, ...(rest as any) };
};

export const useUpdateAccountAdminTask = (): {
  update: (
    payload: UpdateAccountAdminTaskRequest & { accountId: string },
  ) => Promise<AccountAdminTask>;
} & UpdateAccountAdminTaskMutation => {
  const { mutateAsync, ...rest } = useMutation<
    AccountAdminTask,
    GenericForbiddenApiError,
    UpdateAccountAdminTaskRequest & { accountId: string }
  >(
    'updateAccountAdminTask',
    (payload: UpdateAccountAdminTaskRequest & { accountId: string }) => {
      const { accountId, ...request } = payload;
      return client.accountAdminTasks.updateAccountAdminTask(
        request,
        accountId,
      );
    },
  );

  return { update: mutateAsync, ...(rest as any) };
};

export const useDeleteAccountAdminTask = (): {
  delete: (payload: {
    taskId: string;
    accountId: string;
  }) => Promise<MessageResponseModel>;
} & DeleteAccountAdminTaskMutation => {
  const { mutateAsync, ...rest } = useMutation<
    MessageResponseModel,
    GenericForbiddenApiError,
    { taskId: string; accountId: string }
  >(
    'deleteAccountAdminTask',
    (payload: { taskId: string; accountId: string }) =>
      client.accountAdminTasks.deleteAccountAdminTask(
        payload.taskId,
        payload.accountId,
      ),
  );

  return { delete: mutateAsync, ...(rest as any) };
};
